import { Container, IconButton, Tag } from "@fluidattacks/design";
import type { CellContext } from "@tanstack/react-table";
import { camelCase, capitalize } from "lodash";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import type { IGroupData, ITrialData } from "../types";
import { formatVulnerabilityCell, getTrialTip } from "../utils";
import { ManagedType } from "gql/graphql";
import { useOrgUrl } from "pages/organization/hooks";
import { getTagVariant } from "utils/colors";
import { formatLinkHandler } from "utils/format-helpers";
import { translate } from "utils/translations/translate";

const GroupNameFormatter = (
  cell: CellContext<IGroupData, unknown>,
): JSX.Element | string => {
  const { url } = useOrgUrl();
  const groupName = String(cell.getValue()).toLowerCase();
  const isSuspended = cell.row.original.managed === ManagedType.UnderReview;
  const link = `${url}/groups/${groupName}`;
  const text = cell.getValue<string>();

  return isSuspended ? capitalize(text) : formatLinkHandler(link, text);
};

const GroupStatusFormatter = (
  cell: CellContext<IGroupData, unknown>,
  organizationTrial: ITrialData | undefined,
): JSX.Element => {
  const { url } = useOrgUrl();
  const navigate = useNavigate();

  const status = cell.getValue<string>();
  const formatedStatus: string = capitalize(status);
  const currentStateVariant = getTagVariant(capitalize(status));
  const tagText = organizationTrial
    ? translate.t("organization.tabs.groups.status.trial")
    : formatedStatus;

  const link = `${url}/groups/${String(cell.row.getValue("name"))}/scope`;
  const showSuspendedTip =
    cell.row.original.managed === ManagedType.UnderReview;
  const infoTip = [
    organizationTrial && getTrialTip(organizationTrial),
    showSuspendedTip &&
      translate.t("organization.tabs.groups.status.underReviewTip"),
  ].find(Boolean) as string | undefined;

  const handleClick = useCallback((): void => {
    navigate(link);
  }, [link, navigate]);

  return (
    <Container alignItems={"center"} display={"flex"}>
      <Tag priority={"high"} tagLabel={tagText} variant={currentStateVariant} />
      <IconButton
        icon={"arrow-up-right-from-square"}
        iconSize={"sm"}
        iconType={"fa-light"}
        onClick={handleClick}
        tooltip={infoTip}
        variant={"ghost"}
      />
    </Container>
  );
};

const GroupVulnerabilitiesFormatter = (
  cell: CellContext<IGroupData, unknown>,
  rootsLoading: boolean,
): JSX.Element | string => {
  const { url } = useOrgUrl();

  const isSuspended = cell.row.original.managed === ManagedType.UnderReview;
  const link = `${url}/groups/${String(cell.row.getValue("name"))}`;
  const text = rootsLoading ? "Loading..." : cell.getValue<string>();

  return isSuspended ? text : formatVulnerabilityCell(text, link);
};

const GroupRoleFormatter = (cell: CellContext<IGroupData, unknown>): string => {
  return translate.t(`userModal.roles.${camelCase(String(cell.getValue()))}`, {
    defaultValue: "-",
  });
};

const GroupEventsFormatter = (
  cell: CellContext<IGroupData, unknown>,
  eventsLoading: boolean,
): JSX.Element | string => {
  const { url } = useOrgUrl();

  const isSuspended = cell.row.original.managed === ManagedType.UnderReview;
  const link = `${url}/groups/${String(cell.row.getValue("name"))}/events`;

  const text = eventsLoading ? "Loading..." : cell.getValue<string>();

  return isSuspended ? text : formatLinkHandler(link, text);
};

const GroupPlanFormatter = (
  cell: CellContext<IGroupData, unknown>,
): JSX.Element => {
  const planLabels: Record<string, string> = {
    Advanced: translate.t("searchFindings.servicesTable.advanced"),
    Essential: translate.t("searchFindings.servicesTable.essential"),
    Oneshot: "Oneshot",
  };

  const plan = cell.getValue<string>();
  const isTrial = cell.row.original.managed === "TRIAL";

  const formatedPlan: string = isTrial
    ? translate.t("organization.tabs.groups.status.trial")
    : planLabels[plan];

  return <Tag tagLabel={formatedPlan} variant={"inactive"} />;
};

export {
  GroupNameFormatter,
  GroupPlanFormatter,
  GroupRoleFormatter,
  GroupStatusFormatter,
  GroupVulnerabilitiesFormatter,
  GroupEventsFormatter,
};
